import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Loader2, CheckCircle2, ArrowLeft, AlertCircle, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { BOOK } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const PIECES = Array.from({ length: 40 });

function Confetti() {
  const colors = ["#D4AF37", "#E9D7A0", "#ffffff", "#B8902B"];
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      {PIECES.map((_, i) => (
        <motion.span key={i}
          initial={{ y: -40, x: `${Math.random() * 100}vw`, rotate: 0, opacity: 1 }}
          animate={{ y: "110vh", rotate: 360 * (Math.random() > 0.5 ? 1 : -1) }}
          transition={{ duration: 3 + Math.random() * 2, delay: Math.random() * 1.5, repeat: Infinity, ease: "linear" }}
          style={{ position: "absolute", width: 9, height: 14, background: colors[i % colors.length], borderRadius: 2 }} />
      ))}
    </div>
  );
}

export default function ThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading"); // loading | paid | pending | error
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async (attempt) => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(res.data);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (res.data.status === "expired") { setState("error"); return; }
        if (attempt >= MAX_ATTEMPTS) { setState("pending"); return; }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempt >= MAX_ATTEMPTS) { setState("error"); return; }
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-navy text-white grain relative flex items-center justify-center px-5 py-16">
      {state === "paid" && <Confetti />}
      <div className="relative z-10 w-full max-w-2xl text-center">
        {state === "loading" && (
          <div data-testid="thankyou-loading" className="flex flex-col items-center gap-4 text-white/70">
            <Loader2 className="h-10 w-10 animate-spin text-gold" />
            <p>Confirming your pre-order…</p>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="thankyou-pending" className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-gold" />
            <h1 className="font-display text-2xl font-bold">Payment is processing</h1>
            <p className="text-white/70">This can take a moment. Check your email for your confirmation and bonus links.</p>
            <Button asChild className="bg-gold text-navy font-bold rounded-full mt-2">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="thankyou-error" className="flex flex-col items-center gap-4">
            <AlertCircle className="h-12 w-12 text-gold" />
            <h1 className="font-display text-2xl font-bold">We couldn't confirm this order</h1>
            <p className="text-white/70">If you completed payment, your bonuses were emailed to you. Otherwise, please try again.</p>
            <Button asChild className="bg-gold text-navy font-bold rounded-full mt-2">
              <Link to="/"><ArrowLeft className="mr-2 h-4 w-4" /> Return to pre-order</Link>
            </Button>
          </div>
        )}

        {state === "paid" && data && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 border border-gold/40 mb-6">
              <CheckCircle2 className="h-9 w-9 text-gold" />
            </div>
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-gold">Pre-order confirmed</div>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3" data-testid="thankyou-title">
              Thank you{data.customer_name ? `, ${data.customer_name.split(" ")[0]}` : ""}!
            </h1>
            <p className="text-lg text-white/70 mt-4 max-w-lg mx-auto">
              Your copy of <span className="text-white font-semibold">{BOOK.title}</span> is reserved. Download your 3
              AI bonus guides below — we've also emailed them to {data.customer_email}.
            </p>

            <div className="mt-10 space-y-4 text-left">
              {data.bonuses?.map((b, i) => (
                <a key={b.slug} href={b.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`download-${b.slug}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-gold/40 px-5 py-5 transition-all">
                  <div className="flex items-center gap-4">
                    <span className="h-10 w-10 shrink-0 rounded-lg bg-gold text-navy font-display font-extrabold flex items-center justify-center">{i + 1}</span>
                    <div>
                      <div className="font-bold leading-snug">{b.title}</div>
                      <div className="text-sm text-white/50">PDF guide · instant download</div>
                    </div>
                  </div>
                  <span className="flex items-center gap-2 text-gold font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    <Download className="h-5 w-5" />
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-center gap-2 text-sm text-white/50">
              <Mail className="h-4 w-4" /> A receipt + these links were sent to your inbox.
            </div>
            <Button asChild variant="ghost" className="mt-6 text-white/70 hover:text-white">
              <Link to="/"><ArrowLeft className="mr-2 h-4 w-4" /> Back to home</Link>
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
